import React, { FC } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { toast, ToastOptions } from 'react-toastify';

export type BlackToastProps = {
  title?: string;
  text?: string;
  image?: string;
};

const TITLE_FADE_DURATION = 0.35;
const BODY_REVEAL_DELAY = 0.4;
const BODY_REVEAL_DURATION = 0.4;

export const BlackToast: FC<BlackToastProps> = ({ title, text, image }) => {
  const hasBody = !!text || !!image;

  return (
    <Wrapper
      initial={{ opacity: 0, y: 12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: TITLE_FADE_DURATION, ease: `easeOut` }}
    >
      {title && (
        <Title
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: TITLE_FADE_DURATION, ease: `easeOut` }}
        >
          {title}
        </Title>
      )}
      {hasBody && (
        <Body
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: `auto`, opacity: 1 }}
          transition={{
            duration: BODY_REVEAL_DURATION,
            delay: BODY_REVEAL_DELAY,
            ease: `easeOut`,
          }}
        >
          {image && <Image src={image} alt={title || `notification`} />}
          {text && <Text>{text}</Text>}
        </Body>
      )}
    </Wrapper>
  );
};

export type ShowBlackToastOptions = ToastOptions & {
  onClick?: (event: React.MouseEvent) => void;
};

export const showBlackToast = (
  props: BlackToastProps,
  options?: ShowBlackToastOptions,
) =>
  toast(<BlackToast {...props} />, {
    className: `black-toast-container`,
    bodyClassName: `black-toast-body`,
    closeButton: false,
    hideProgressBar: true,
    ...options,
  });

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 20px 24px;
  border-radius: 24px;
  background: #000;
  color: #fff;
  overflow: hidden;
`;

const Title = styled(motion.div)`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
`;

const Body = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
`;

const Text = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
`;

const Image = styled.img`
  width: 100%;
  border-radius: 16px;
  display: block;
  object-fit: cover;
`;
